/**
 * LowMemoryStatus — visualises which background schedulers are
 * active on the running backend, so the user can confirm the
 * Starter-tier LOW_MEMORY_MODE takes effect after redeploy.
 *
 * Route: /low-memory-mode  (admin-only)
 * Backend: GET /api/system/lowmem-status
 */
import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Activity, Zap, ZapOff, RefreshCw, Loader2, ShieldCheck } from 'lucide-react';

const ROW_ORDER = [
  { name: 'nightly_backup',      ar: 'النسخ الاحتياطي الليلي',        cadence: 'يومياً 02:00' },
  { name: 'recon_snap',           ar: 'لقطة التسوية اليومية',           cadence: 'يومياً 06:15' },
  { name: 'notification_retry',   ar: 'إعادة إرسال الإشعارات',          cadence: 'دورياً' },
  { name: 'governance_snapshot',  ar: 'لقطة الحوكمة',                    cadence: 'يومياً 06:00' },
  { name: 'health_monitor',       ar: 'مراقب الصحة',                     cadence: 'يومياً 06:00' },
  { name: 's2p',                  ar: 'محرك المشتريات إلى الدفع',         cadence: 'يومياً 05:30' },
  { name: 'reservation_watchdog', ar: 'مراقب انتهاء الحجوزات',           cadence: 'يومياً 05:45' },
  { name: 'cfo_brief',            ar: 'موجز المدير المالي اليومي',        cadence: 'يومياً 06:00' },
  { name: 'dvl_gap',              ar: 'كاشف الفجوات DVL',                cadence: 'يومياً 04:00' },
  { name: 'workflow_escalation',  ar: 'تصعيد سير العمل',                 cadence: 'كل 30 دقيقة' },
];

const LowMemoryStatus = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await axiosInstance.get('/system/lowmem-status');
      setData(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تحميل الحالة');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="lowmem-status-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-600" />
            حالة الذاكرة والجدولات الخلفية
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            يعرض ما إذا كان وضع Low Memory مفعّلاً وأي الجدولات نشِطة الآن.
          </p>
        </div>
        <button onClick={load}
                data-testid="lowmem-refresh-btn"
                className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm
                            hover:bg-slate-50 flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          تحديث
        </button>
      </div>

      {data && (
        <>
          {/* Mode banner */}
          <div className={`rounded-lg border-2 p-4 mb-4 ${
            data.low_memory_mode
              ? 'border-emerald-400 bg-emerald-50'
              : 'border-slate-300 bg-slate-50'
          }`}
               data-testid="lowmem-banner">
            <div className="flex items-center gap-3">
              {data.low_memory_mode ? (
                <ShieldCheck className="w-8 h-8 text-emerald-600" />
              ) : (
                <Activity className="w-8 h-8 text-slate-600" />
              )}
              <div>
                <div className="font-bold text-lg">
                  {data.low_memory_mode
                    ? 'وضع الذاكرة المنخفض مُفعَّل ✓'
                    : 'الوضع القياسي (كل الجدولات تعمل)'}
                </div>
                <div className="text-[13px] text-slate-700 mt-0.5">
                  {data.low_memory_mode
                    ? `الجدولات الاختيارية معطَّلة. توفير تقديري 80-120 MB. `
                       + `notification_retry يعمل كل ${data.loop_periods_seconds?.notification_retry ?? 30}ث.`
                    : 'لتفعيل توفير الذاكرة على Starter tier: أضف LOW_MEMORY_MODE=true في backend/.env.'}
                </div>
              </div>
            </div>
          </div>

          {/* Schedulers grid */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-4">
            <div className="bg-slate-100 px-4 py-2 font-semibold text-sm">
              حالة الجدولات ({Object.values(data.schedulers || {}).filter(s => s.enabled).length} / {Object.keys(data.schedulers || {}).length} نشطة)
            </div>
            <table className="w-full text-sm">
              <thead className="text-[11px] text-slate-600 bg-slate-50">
                <tr>
                  <th className="text-right px-3 py-2">الجدولة</th>
                  <th className="text-right px-3 py-2">الوصف</th>
                  <th className="text-right px-3 py-2">التكرار</th>
                  <th className="text-center px-3 py-2">الحالة</th>
                  <th className="text-right px-3 py-2">Override</th>
                </tr>
              </thead>
              <tbody>
                {ROW_ORDER.map((row) => {
                  const info = data.schedulers?.[row.name] || {};
                  return (
                    <tr key={row.name}
                        className="border-t border-slate-100 hover:bg-slate-50"
                        data-testid={`scheduler-row-${row.name}`}>
                      <td className="px-3 py-2 font-mono text-[12px]">{row.name}</td>
                      <td className="px-3 py-2 text-[12px]">{row.ar}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-500">{row.cadence}</td>
                      <td className="px-3 py-2 text-center">
                        {info.enabled ? (
                          <span className="inline-flex items-center gap-1 rounded-full
                                            bg-emerald-100 text-emerald-800 px-2 py-0.5
                                            text-[11px] font-semibold"
                                data-testid={`scheduler-${row.name}-enabled`}>
                            <Zap className="w-3 h-3" /> نشطة
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full
                                            bg-slate-200 text-slate-600 px-2 py-0.5
                                            text-[11px] font-semibold"
                                data-testid={`scheduler-${row.name}-disabled`}>
                            <ZapOff className="w-3 h-3" /> معطَّلة
                          </span>
                        )}
                        {info.essential && (
                          <div className="text-[9px] text-indigo-700 mt-0.5">أساسية</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] font-mono text-slate-700">
                        {info.env_override ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Help */}
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-[12px] text-indigo-900 leading-relaxed">
            <div className="font-bold mb-1">كيف تُغيّر الحالة؟</div>
            <ul className="list-disc mr-5 space-y-0.5">
              <li>تفعيل التوفير: أضف <code className="bg-white px-1 rounded">LOW_MEMORY_MODE=true</code> في backend/.env ثم Redeploy.</li>
              <li>إبقاء جدولة نشطة رغم التوفير: <code className="bg-white px-1 rounded">ENABLE_HEALTH_MONITOR_SCHEDULER=true</code></li>
              <li>إطفاء أساسية: <code className="bg-white px-1 rounded">ENABLE_NIGHTLY_BACKUP_SCHEDULER=false</code></li>
              <li>ضبط تردد إعادة إرسال الإشعارات: <code className="bg-white px-1 rounded">NOTIFICATION_RETRY_PERIOD_SECONDS=60</code></li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default LowMemoryStatus;
